import * as API from '../api.js';
import { UI_COPY } from '../config/uiCopy.js';
import { state } from '../state.js';
import { showToast } from './toast.js';

const ADMIN_REPEAT_BATCH_SIZE = 20;
const REPEAT_CACHE_MAX_JOBS = 24;
const FLIPPED_SUFFIX = ' (Flipped)';
const _repeatRowsCache = new Map();

function _activeTriviaJobId() {
    return String(state.viewerJobId || state.selectedHistoryJobId || state.jobId || '').trim();
}

function _historyManifestKeyForJob(jobId) {
    const safeJobId = String(jobId || '').trim();
    const selected = state.selectedHistoryJob || null;
    if (!safeJobId || !selected) return '';
    if (String(selected.job_id || '').trim() !== safeJobId) return '';
    return String(selected.manifest_key || '').trim();
}

function _trimSafePrefix(name) {
    return (name || '').replace(/^[0-9a-fA-F]{8}_/, '');
}

function _normalizeBasePath(file) {
    const safeFile = typeof file === 'string' ? file : String(file || '');
    if (!safeFile) return '';
    return safeFile.endsWith(FLIPPED_SUFFIX) ? safeFile.slice(0, -FLIPPED_SUFFIX.length) : safeFile;
}

function _encodePath(path) {
    return String(path || '').split('/').map(encodeURIComponent).join('/');
}

function _buildRepeatPreviewUrls(libId, basePath) {
    const safeLibId = encodeURIComponent(String(libId || '').trim());
    const encodedPath = _encodePath(basePath);
    return {
        thumbUrl: `/static/libraries/${safeLibId}/.ui_thumbs/${encodedPath}`,
        fullUrl: `/static/libraries/${safeLibId}/${encodedPath}`,
    };
}

function _applyRepeatPreviewSource(img, urls) {
    if (!img || !urls) return;
    let didFallback = false;
    img.onerror = () => {
        if (didFallback) {
            img.onerror = null;
            return;
        }
        didFallback = true;
        img.onerror = null;
        img.src = urls.fullUrl;
    };
    img.src = urls.thumbUrl;
}

function _touchRepeatCache(jobId, entry) {
    if (!jobId || !entry) return entry || null;
    if (_repeatRowsCache.has(jobId)) _repeatRowsCache.delete(jobId);
    _repeatRowsCache.set(jobId, entry);
    while (_repeatRowsCache.size > REPEAT_CACHE_MAX_JOBS) {
        const oldestKey = _repeatRowsCache.keys().next().value;
        if (!oldestKey) break;
        _repeatRowsCache.delete(oldestKey);
    }
    return entry;
}

function _getRepeatCache(jobId) {
    const safeJobId = String(jobId || '').trim();
    if (!safeJobId) return null;
    const entry = _repeatRowsCache.get(safeJobId) || null;
    if (!entry) return null;
    return _touchRepeatCache(safeJobId, entry);
}

function _ensureRepeatCache(jobId, initialRows = [], context = {}) {
    const safeJobId = String(jobId || '').trim();
    if (!safeJobId) return null;
    const existing = _getRepeatCache(safeJobId);
    if (existing) {
        if (!existing.manifestKey && context.manifestKey) {
            existing.manifestKey = String(context.manifestKey || '').trim();
        }
        const minVisible = Array.isArray(initialRows) ? initialRows.length : 0;
        if (!Number.isFinite(existing.visibleCount) || existing.visibleCount < minVisible) {
            existing.visibleCount = minVisible;
        }
        return existing;
    }
    const entry = {
        items: null,
        total: null,
        visibleCount: Array.isArray(initialRows) ? initialRows.length : 0,
        loading: false,
        error: '',
        inflight: null,
        manifestKey: String(context.manifestKey || '').trim(),
    };
    return _touchRepeatCache(safeJobId, entry);
}

function _buildRepeatedRowsFromMap(tileMap) {
    const counts = new Map();
    let firstSeen = 0;
    for (const raw of (Array.isArray(tileMap) ? tileMap : [])) {
        const basePath = _normalizeBasePath(raw && raw.file);
        if (!basePath) continue;
        if (!counts.has(basePath)) {
            counts.set(basePath, { file: basePath, count: 0, firstSeen });
            firstSeen += 1;
        }
        const current = counts.get(basePath);
        current.count += 1;
    }
    return Array.from(counts.values())
        .filter((row) => Number(row.count || 0) > 1)
        .sort((left, right) => {
            const countDelta = Number(right.count || 0) - Number(left.count || 0);
            if (countDelta !== 0) return countDelta;
            return Number(left.firstSeen || 0) - Number(right.firstSeen || 0);
        })
        .map(({ file, count }) => ({ file, count }));
}

function _ensureAdminControls(tvTopThumbs) {
    if (!tvTopThumbs) return null;
    const parent = tvTopThumbs.parentElement;
    if (!parent) return null;
    let controls = document.getElementById('tvTopAdminControls');
    if (!controls) {
        controls = document.createElement('div');
        controls.id = 'tvTopAdminControls';
        controls.className = 'trivia-admin-controls hidden';
        controls.setAttribute('aria-live', 'polite');
        parent.appendChild(controls);
    }
    return controls;
}

function _setAdminControlsHidden(tvTopThumbs) {
    const controls = _ensureAdminControls(tvTopThumbs);
    if (!controls) return;
    controls.innerHTML = '';
    controls.classList.add('hidden');
    if (tvTopThumbs) {
        tvTopThumbs.onscroll = null;
    }
}

function _currentTileMapForJob(jobId) {
    const safeJobId = String(jobId || '').trim();
    if (!safeJobId) return [];
    if (_activeTriviaJobId() !== safeJobId) return [];
    return Array.isArray(state.tileMap) ? state.tileMap : [];
}

async function _loadAllRepeatRows({ jobId, manifest, manifestKey, cacheEntry }) {
    if (cacheEntry && Array.isArray(cacheEntry.items)) {
        return { rows: cacheEntry.items.slice(), source: 'cache' };
    }
    if (cacheEntry && cacheEntry.inflight) {
        return cacheEntry.inflight;
    }

    const loadPromise = (async () => {
        const manifestMap = Array.isArray(manifest && manifest.map) ? manifest.map : [];
        if (manifestMap.length) {
            return { rows: _buildRepeatedRowsFromMap(manifestMap), source: 'manifest_map' };
        }

        const currentTileMap = _currentTileMapForJob(jobId);
        if (currentTileMap.length) {
            return { rows: _buildRepeatedRowsFromMap(currentTileMap), source: 'state_tile_map' };
        }

        const safeManifestKey = String(manifestKey || '').trim();
        if (!safeManifestKey) {
            throw new Error('repeat_map_unavailable');
        }

        const fullManifest = await API.getManifestByKey(safeManifestKey, { includeMap: true });
        const fullMap = Array.isArray(fullManifest && fullManifest.map) ? fullManifest.map : [];
        if (!fullMap.length) {
            throw new Error('repeat_map_unavailable');
        }
        if (_activeTriviaJobId() === String(jobId || '').trim()) {
            state.tileMap = fullMap;
        }
        return { rows: _buildRepeatedRowsFromMap(fullMap), source: 'manifest_fetch' };
    })();

    if (cacheEntry) {
        cacheEntry.inflight = loadPromise;
    }
    try {
        return await loadPromise;
    } finally {
        if (cacheEntry && cacheEntry.inflight === loadPromise) {
            cacheEntry.inflight = null;
        }
    }
}

function _buildAdminStatusText({
    visibleCount = 0,
    totalCount = null,
    loading = false,
    error = '',
    hasMore = false,
} = {}) {
    const safeVisible = Math.max(0, Number(visibleCount || 0));
    const safeTotal = totalCount == null ? null : Math.max(0, Number(totalCount || 0));
    if (loading) {
        return `Admin view: loading ${ADMIN_REPEAT_BATCH_SIZE} more repeated photos...`;
    }
    if (error) {
        return String(error);
    }
    if (safeTotal == null) {
        return hasMore
            ? `Admin view: showing ${safeVisible} repeated photos. Scroll to the end to load ${ADMIN_REPEAT_BATCH_SIZE} more.`
            : `Admin view: showing ${safeVisible} repeated photos.`;
    }
    if (safeVisible >= safeTotal) {
        return `Admin view: showing all ${safeTotal} repeated photos.`;
    }
    const remaining = Math.max(0, safeTotal - safeVisible);
    const nextBatch = Math.max(1, Math.min(ADMIN_REPEAT_BATCH_SIZE, remaining));
    return `Admin view: showing ${safeVisible} of ${safeTotal} repeated photos. Scroll to the end to load ${nextBatch} more.`;
}

async function _loadNextRepeatBatch({
    jobId,
    trivia,
    manifest,
    context,
    initialRows,
    trigger = 'scroll_end',
}) {
    const manifestKey = String(
        (context && context.manifestKey)
        || _historyManifestKeyForJob(jobId)
        || ''
    ).trim();
    const activeCache = _ensureRepeatCache(jobId, initialRows, { manifestKey });
    if (!activeCache || activeCache.loading) return false;

    const startedAt = (typeof performance !== 'undefined' && typeof performance.now === 'function')
        ? performance.now()
        : Date.now();
    const currentVisibleCount = Array.isArray(activeCache.items)
        ? Math.min(Math.max(initialRows.length, Number(activeCache.visibleCount || 0)), activeCache.items.length)
        : Math.max(initialRows.length, Number(activeCache.visibleCount || 0));

    if (Array.isArray(activeCache.items) && activeCache.items.length) {
        if (currentVisibleCount >= activeCache.items.length) return false;
        activeCache.visibleCount = Math.min(currentVisibleCount + ADMIN_REPEAT_BATCH_SIZE, activeCache.items.length);
        activeCache.error = '';
        if (_activeTriviaJobId() === jobId) {
            renderTrivia(trivia, manifest, context);
        }
        const durationMs = (
            (typeof performance !== 'undefined' && typeof performance.now === 'function')
                ? performance.now()
                : Date.now()
        ) - startedAt;
        API.logUiFlow('viewer_repeats', 'load_more', 'ok', {
            job_id: jobId,
            source: 'cache',
            trigger,
            visible_count: activeCache.visibleCount,
            returned: Math.max(0, activeCache.visibleCount - currentVisibleCount),
            total_available: activeCache.items.length,
        }, durationMs).catch(() => {});
        return true;
    }

    activeCache.loading = true;
    activeCache.error = '';
    if (_activeTriviaJobId() === jobId) {
        renderTrivia(trivia, manifest, context);
    }

    try {
        const { rows, source } = await _loadAllRepeatRows({
            jobId,
            manifest,
            manifestKey: activeCache.manifestKey || manifestKey,
            cacheEntry: activeCache,
        });
        activeCache.items = Array.isArray(rows) ? rows : [];
        activeCache.total = activeCache.items.length;
        activeCache.visibleCount = Math.min(currentVisibleCount + ADMIN_REPEAT_BATCH_SIZE, activeCache.items.length);
        activeCache.loading = false;
        activeCache.error = '';
        const durationMs = (
            (typeof performance !== 'undefined' && typeof performance.now === 'function')
                ? performance.now()
                : Date.now()
        ) - startedAt;
        API.logUiFlow('viewer_repeats', 'load_more', 'ok', {
            job_id: jobId,
            source,
            trigger,
            visible_count: activeCache.visibleCount,
            returned: Math.max(0, activeCache.visibleCount - currentVisibleCount),
            total_available: activeCache.items.length,
        }, durationMs).catch(() => {});
        return activeCache.visibleCount > currentVisibleCount;
    } catch (err) {
        activeCache.loading = false;
        activeCache.error = 'Could not load more repeated photos. Try again.';
        const durationMs = (
            (typeof performance !== 'undefined' && typeof performance.now === 'function')
                ? performance.now()
                : Date.now()
        ) - startedAt;
        API.logUiFlow('viewer_repeats', 'load_more', 'error', {
            job_id: jobId,
            trigger,
            message: String((err && err.message) || err || 'load_failed').slice(0, 160),
        }, durationMs).catch(() => {});
        showToast('Most used photos', 'Could not load more repeated photos. Try again.', 3200);
        return false;
    } finally {
        if (_activeTriviaJobId() === jobId) {
            renderTrivia(trivia, manifest, context);
        }
    }
}

function _renderRepeatAdminControls({
    tvTopThumbs,
    trivia,
    manifest,
    context,
    libId,
    initialRows,
}) {
    const controls = _ensureAdminControls(tvTopThumbs);
    if (!controls) return;

    const jobId = String((context && context.jobId) || _activeTriviaJobId()).trim();
    const manifestKey = String(
        (context && context.manifestKey)
        || _historyManifestKeyForJob(jobId)
        || ''
    ).trim();
    const hasTopRepeated = Array.isArray(trivia && trivia.top_repeated);
    const isAdmin = Boolean(state.isAdmin);
    const hasRepeatRowSource = Boolean(
        (Array.isArray(manifest && manifest.map) && manifest.map.length > 0)
        || _currentTileMapForJob(jobId).length > 0
        || manifestKey
    );
    const currentRowsLookTruncated = hasTopRepeated
        ? initialRows.length >= ADMIN_REPEAT_BATCH_SIZE
        : initialRows.length >= 10;
    const canBootstrapLoadMore = Boolean(
        jobId
        && libId
        && hasRepeatRowSource
        && currentRowsLookTruncated
    );

    if (!isAdmin || !jobId || !libId) {
        controls.innerHTML = '';
        controls.classList.add('hidden');
        tvTopThumbs.onscroll = null;
        return;
    }

    const cacheEntry = _ensureRepeatCache(jobId, initialRows, { manifestKey });
    const totalKnown = Array.isArray(cacheEntry && cacheEntry.items)
        ? cacheEntry.items.length
        : (Number.isFinite(cacheEntry && cacheEntry.total) ? cacheEntry.total : null);
    const visibleCount = Array.isArray(cacheEntry && cacheEntry.items)
        ? Math.min(
            Math.max(initialRows.length, Number(cacheEntry.visibleCount || 0)),
            cacheEntry.items.length
        )
        : Math.max(initialRows.length, Number((cacheEntry && cacheEntry.visibleCount) || 0));
    const hasMore = totalKnown != null ? visibleCount < totalKnown : canBootstrapLoadMore;
    const shouldShowControls = Boolean(hasMore || (cacheEntry && cacheEntry.loading) || (cacheEntry && cacheEntry.error) || totalKnown != null);

    controls.innerHTML = '';
    controls.classList.toggle('hidden', !shouldShowControls);
    if (!shouldShowControls) return;

    const status = document.createElement('div');
    status.className = `trivia-admin-status hint${cacheEntry && cacheEntry.error ? ' is-error' : ''}`;
    status.textContent = _buildAdminStatusText({
        visibleCount,
        totalCount: totalKnown,
        loading: Boolean(cacheEntry && cacheEntry.loading),
        error: (cacheEntry && cacheEntry.error) || '',
        hasMore,
    });
    controls.appendChild(status);
    tvTopThumbs.onscroll = hasMore
        ? () => {
            if (!Boolean(state.isAdmin) || _activeTriviaJobId() !== jobId) return;
            const remainingPx = tvTopThumbs.scrollWidth - (tvTopThumbs.scrollLeft + tvTopThumbs.clientWidth);
            if (remainingPx > 24) return;
            void _loadNextRepeatBatch({
                jobId,
                trivia,
                manifest,
                context,
                initialRows,
                trigger: 'scroll_end',
            });
        }
        : null;
}

export function renderTrivia(trivia, manifest = null, context = {}) {
    const card = document.getElementById('triviaCard');
    if (!card) return;
    if (!trivia) {
        card.classList.add('hidden');
        _setAdminControlsHidden(document.getElementById('tvTopThumbs'));
        return;
    }

    const tvTime = document.getElementById('tvTime');
    const tvTotal = document.getElementById('tvTotal');
    const tvUnique = document.getElementById('tvUnique');
    const tvFlips = document.getElementById('tvFlips');
    const tvTopThumbs = document.getElementById('tvTopThumbs');
    const duration = manifest?.meta?.duration_seconds ?? manifest?.timing?.total_job_s ?? null;
    if (tvTime) {
        if (duration === null || duration === undefined) tvTime.innerText = '-';
        else tvTime.innerText = `${Math.round(duration)}s`;
    }

    if (tvTotal) tvTotal.innerText = trivia.total_tiles_placed ?? '-';
    if (tvUnique) tvUnique.innerText = trivia.unique_photos_used ?? '-';
    if (tvFlips) tvFlips.innerText = trivia.flipped_tiles_used ?? 0;

    const libId = manifest?.settings?.library_id ?? null;
    const jobId = String((context && context.jobId) || _activeTriviaJobId()).trim();
    const manifestKey = String(
        (context && context.manifestKey)
        || _historyManifestKeyForJob(jobId)
        || ''
    ).trim();
    if (tvTopThumbs) tvTopThumbs.innerHTML = '';

    let repeats = Array.isArray(trivia.top_repeated) ? trivia.top_repeated.slice() : (trivia.top10_used || []).slice();
    repeats = repeats.filter((row) => Number((row && row.count) || 0) > 1).slice(0, ADMIN_REPEAT_BATCH_SIZE);

    const cacheEntry = jobId ? _ensureRepeatCache(jobId, repeats, { manifestKey }) : null;
    const renderRows = (Boolean(state.isAdmin) && cacheEntry && Array.isArray(cacheEntry.items) && cacheEntry.items.length)
        ? cacheEntry.items.slice(
            0,
            Math.min(
                Math.max(repeats.length, Number(cacheEntry.visibleCount || 0)),
                cacheEntry.items.length
            )
        )
        : repeats;

    if (!tvTopThumbs) {
        card.classList.remove('hidden');
        return;
    }

    const renderHint = (text) => {
        tvTopThumbs.innerHTML = '';
        const hint = document.createElement('div');
        hint.className = 'hint';
        hint.textContent = text;
        tvTopThumbs.appendChild(hint);
    };

    if (!renderRows.length) {
        renderHint(UI_COPY.trivia.noRepeats);
        _setAdminControlsHidden(tvTopThumbs);
    } else if (!libId) {
        renderHint(UI_COPY.trivia.missingLibrary);
        _setAdminControlsHidden(tvTopThumbs);
    } else {
        renderRows.forEach((row) => {
            const basePath = _normalizeBasePath(row && row.file);
            const baseName = basePath.split('/').pop();
            const nice = _trimSafePrefix(baseName);
            const urls = _buildRepeatPreviewUrls(libId, basePath);

            const wrap = document.createElement('div');
            wrap.className = 'trivia-thumb';

            const img = document.createElement('img');
            img.loading = 'lazy';
            img.decoding = 'async';
            img.alt = nice;
            _applyRepeatPreviewSource(img, urls);

            const meta = document.createElement('div');
            meta.className = 'meta';
            const countSpan = document.createElement('span');
            countSpan.className = 'count';
            countSpan.textContent = `${row.count}x`;
            const nameSpan = document.createElement('span');
            nameSpan.className = 'name';
            nameSpan.title = nice;
            nameSpan.textContent = nice;
            meta.appendChild(countSpan);
            meta.appendChild(nameSpan);

            wrap.appendChild(img);
            wrap.appendChild(meta);
            tvTopThumbs.appendChild(wrap);
        });

        _renderRepeatAdminControls({
            tvTopThumbs,
            trivia,
            manifest,
            context: {
                jobId,
                manifestKey,
            },
            libId,
            initialRows: repeats,
        });
    }

    card.classList.remove('hidden');
}
